// Play on the website never runs a game in the page. Every Play button opens the launcher through thrdm://play/<g_id>;
// if nothing takes the link, we send people to the download page. Beside it, "Play in browser" (components.ts) opens a
// web game's own site in a new tab, so Windows and anyone without the app can still play (owner, 2026-10-18).

const GAME_ID = /^g_[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function playLink(gameId: string): string {
  if (!GAME_ID.test(gameId)) throw new Error(`not a game id: ${gameId}`);
  return `thrdm://play/${gameId}`;
}

/**
 * Things you do "in Threedom" from a game page: tip its creator, or join its waitlist. The website never takes money
 * or keeps a waitlist itself; the launcher opens the game's page there with the action (deep links proposed in wave 6,
 * the launcher's links.rs has to learn them). Without the launcher, the click falls back to the download page.
 */
export type GameAction = "tip" | "notify";

export function gameActionLink(gameId: string, action: GameAction): string {
  if (!GAME_ID.test(gameId)) throw new Error(`not a game id: ${gameId}`);
  if (action !== "tip" && action !== "notify") throw new Error(`not a game action: ${String(action)}`);
  return `thrdm://game/${gameId}?action=${action}`;
}

export function downloadPath(gameId?: string, action?: GameAction): string {
  if (!gameId || !GAME_ID.test(gameId)) return "/download";
  return `/download?game=${encodeURIComponent(gameId)}${action === "tip" || action === "notify" ? `&for=${action}` : ""}`;
}

/**
 * Hand the deep link to the OS. When the launcher is installed the page loses focus (the OS switches apps or shows
 * its "open thrdm?" prompt); if the page is still focused and visible after `waitMs`, nothing handled the link.
 */
export function launch(gameId: string, opts: { waitMs?: number; onMissing?: () => void; link?: string } = {}): void {
  const link = opts.link ?? playLink(gameId);
  let handled = false;
  const mark = () => (handled = true);
  window.addEventListener("blur", mark, { once: true });
  document.addEventListener("visibilitychange", mark, { once: true });
  window.addEventListener("pagehide", mark, { once: true });
  window.location.href = link;
  window.setTimeout(() => {
    window.removeEventListener("blur", mark);
    document.removeEventListener("visibilitychange", mark);
    window.removeEventListener("pagehide", mark);
    if (!handled && document.visibilityState === "visible" && document.hasFocus()) opts.onMissing?.();
  }, opts.waitMs ?? 1600);
}

export type Platform = "mac" | "linux" | "windows" | "mobile" | "other";

export function detectPlatform(ua: string): Platform {
  if (/iphone|ipad|android|mobile/i.test(ua)) return "mobile";
  if (/mac os x|macintosh/i.test(ua)) return "mac";
  if (/windows/i.test(ua)) return "windows";
  if (/linux|x11/i.test(ua)) return "linux";
  return "other";
}
